import { Group } from "@visx/group";
import { withParentSize } from "@visx/responsive";
import { scaleOrdinal } from "@visx/scale";
import { Pie } from "@visx/shape";
import classnames from "classnames/bind";
import React, { useCallback, useContext, useRef, useState } from "react";
import {
  ChartTooltipContext,
  ChartTooltipWrapper,
} from "../components/ChartTooltip";
import { MULTICOLOUR_LINE_COLOURS, OTHER } from "../constants";
import Arc from "./Arc";
import styles from "./PieChart.module.scss";
import { PieChartLegend } from "./PieChartLegend";
import { LEGEND_MARGIN } from "./PieChartLegend/constants";
import { PieChartTooltip } from "./PieChartTooltip";
import type { PieChartProps } from "./types";

const cx = classnames.bind(styles);

const PieChart = <T extends Record<string, any>>({
  parentWidth,
  data,
  labelAccessor,
  valueAccessor,
  valueFormatter,
  orientation = "horizontal",
  isPieChartCentered = false,
}: PieChartProps<T>) => {
  const { showTooltip, hideTooltip, containerBounds } =
    useContext(ChartTooltipContext);

  const legendRef = useRef<HTMLDivElement>(null);

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(
    undefined
  );

  const legendWidth = (legendRef?.current?.clientWidth ?? 0) + LEGEND_MARGIN;

  const diameter =
    orientation === "horizontal" ? parentWidth - legendWidth : parentWidth;

  const radius = diameter / 2;

  const donutThickness = radius * 0.6;

  const colorScale = scaleOrdinal<string, string>({
    domain: data.map((item) => labelAccessor(item)),
    range: MULTICOLOUR_LINE_COLOURS,
  });

  const onChangeHovered = (newIndex?: number) => {
    setHoveredIndex(newIndex);
  };

  const onPointerMove = useCallback(
    (event: React.PointerEvent<SVGSVGElement>, point) => {
      if (point) {
        onChangeHovered(point.index);

        if (showTooltip) {
          const containerX =
            ("clientX" in event ? event.clientX : 0) - containerBounds.x;

          const containerY =
            ("clientY" in event ? event.clientY : 0) - containerBounds.y;

          const label = labelAccessor(point);

          const value = valueAccessor(point);

          showTooltip({
            tooltipLeft: containerX,
            tooltipTop: containerY,
            tooltipData: point,
            component: (
              <PieChartTooltip
                tooltipData={point}
                label={label}
                value={valueFormatter?.(value) ?? value}
              />
            ),
          });
        }
      } else {
        if (hideTooltip) {
          hideTooltip();
        }

        onChangeHovered(undefined);
      }
    },
    [showTooltip]
  );

  const getFillColor = (hexColor: string, index: number) => {
    if (hoveredIndex !== undefined) {
      if (index !== hoveredIndex) {
        // Set the opacity to 50%
        return `${hexColor}50`;
      }
    }

    return hexColor;
  };

  return (
    <div
      className={cx("container", orientation, {
        centerPieChart: isPieChartCentered,
      })}
    >
      <svg
        width={diameter}
        height={diameter}
      >
        <Group
          top={radius}
          left={radius}
        >
          <Pie
            data={data}
            pieValue={valueAccessor}
            outerRadius={radius}
            innerRadius={radius - donutThickness}
            cornerRadius={3}
            padAngle={0.005}
          >
            {(pie) => {
              return pie.arcs.map((arc, index) => {
                const label = labelAccessor(arc.data);
                const fill =
                  label === OTHER.LABEL
                    ? OTHER.COLOUR
                    : getFillColor(colorScale(label), index);

                const props = {
                  label,
                  value: valueAccessor(arc.data),
                  path: pie.path(arc)?.toString(),
                  fill,
                  onPointerMove: (
                    event:
                      | React.PointerEvent<SVGElement>
                      | React.MouseEvent<SVGElement>,
                    point?: {
                      label: string;
                      value: any;
                      path?: string;
                      fill: string;
                    }
                  ) =>
                    onPointerMove(
                      event as React.PointerEvent<SVGSVGElement>,
                      point ? { ...point, index, fill } : undefined
                    ),
                };

                return (
                  <Arc
                    key={label}
                    {...props}
                  />
                );
              });
            }}
          </Pie>
        </Group>
      </svg>

      <PieChartLegend
        data={data}
        active={hoveredIndex}
        orientation={orientation === "horizontal" ? "vertical" : "horizontal"}
        ref={legendRef}
        scale={colorScale}
        onMouseOver={(
          event: React.PointerEvent<SVGElement> | React.MouseEvent<SVGElement>,
          point?: {
            label: string;
            value: any;
            path?: string;
            fill: string;
          }
        ) => {
          onPointerMove(
            event as React.PointerEvent<SVGSVGElement>,
            point ? point : undefined
          );
        }}
      />
    </div>
  );
};

const WrappedPieChart: ReturnType<typeof withParentSize> = withParentSize(
  (props) => (
    // @ts-expect-error: withParentSize type error, not ours
    <ChartTooltipWrapper<PieChartProps>
      {...props}
      component={PieChart}
    />
  )
);

export default WrappedPieChart;
